/**
 * Notification category taxonomy — client mirror.
 * Mirrors server/src/utils/notification-category.ts's NotificationType ->
 * NotificationCategory map. There is no endpoint that exposes this mapping,
 * so it's necessarily duplicated here. If a new type is ever added to the
 * server map, update this file too — nothing enforces the two staying in sync.
 */
import { i18n } from '../i18n';

export const NOTIFICATION_CATEGORIES = [
  'CRITICAL',
  'ACADEMICS',
  'TIMETABLE',
  'LIBRARY',
  'SOCIAL',
  'ANNOUNCEMENTS',
  'SYSTEM',
  // [M129] Mirrors the server bucket. The bus gets its own category so muting attendance (SYSTEM)
  // no longer silences it — muting is by CATEGORY, so a new type inside SYSTEM would fix nothing.
  'TRANSPORT',
  // [REQ-AUG-30 M.1] Mirrors the server bucket — same reasoning as TRANSPORT: LEAVE inside SYSTEM
  // meant muting the attendance chatter also muted "your leave was approved".
  'LEAVE',
  // [REQ-SEP-04 Band F / F4, Q6] Mirrors the server bucket. FEE_DUE leaves CRITICAL the moment it
  // gains its first producer — a recurring dues nag must be mutable and must not raise the
  // full-width CriticalBanner (CRITICAL_TYPES below is DERIVED from this map, so moving the mapping
  // moves the banner too).
  'FEES',
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_CATEGORY: Record<string, NotificationCategory> = {
  EMERGENCY_HOLIDAY: 'CRITICAL',
  FEE_DUE: 'FEES', // [Band F / F4, Q6] mirror of the server map — mutable bucket, no CriticalBanner

  EXAM_RESULT: 'ACADEMICS',
  RECHECK: 'ACADEMICS',
  ACHIEVEMENT: 'ACADEMICS',
  HOMEWORK_REMINDER: 'ACADEMICS',
  HOMEWORK_DIGEST: 'ACADEMICS',
  EXAM_SYLLABUS: 'ACADEMICS',
  SUBSTITUTION: 'TIMETABLE',
  TIMETABLE_CHANGE: 'TIMETABLE',
  ATTENDANCE_DELEGATION: 'TIMETABLE',
  LIBRARY: 'LIBRARY',
  BIRTHDAY: 'SOCIAL',
  FESTIVAL: 'SOCIAL',
  GENERAL: 'SOCIAL',
  ANNOUNCEMENT: 'ANNOUNCEMENTS',
  HOLIDAY: 'ANNOUNCEMENTS',
  PTM: 'ANNOUNCEMENTS',
  BUG_STATUS: 'SYSTEM',
  SUPPORT_TICKET: 'SYSTEM',
  ATTENDANCE: 'SYSTEM',
  TRANSPORT_UPDATE: 'TRANSPORT',
  ATTENDANCE_AUTO_ABSENT: 'SYSTEM', // STAFF-ATT Slice 3 — mirror of the server map
  ATTENDANCE_ADJUSTED: 'SYSTEM', // STAFF-ATT Slice 4 — mirror of the server map
  LEAVE: 'LEAVE', // [M.1] own bucket — mirror of the server map

  CAPTAIN_ASSIGNED: 'SYSTEM',
  CAPTAIN_REMOVED: 'SYSTEM',
  // PHOTO-1 — mirror of the server map. ⚠️ This copy is `Record<string, …>`, so unlike the server's
  // `satisfies Record<NotificationType, …>` an omission here compiles clean and the notification simply
  // falls out of every category filter. That is why it is added in the same commit as the enum.
  PHOTO_APPROVED: 'SYSTEM',
  PHOTO_REJECTED: 'SYSTEM',
};

/** Human-readable label for each category, used by filter tabs across the 4 role notification pages.
 *  English source of truth (staff notification pages render this directly). */
export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  CRITICAL: 'Critical',
  ACADEMICS: 'Academics',
  TIMETABLE: 'Timetable',
  LIBRARY: 'Library',
  SOCIAL: 'Social',
  ANNOUNCEMENTS: 'Announcements',
  SYSTEM: 'System',
  // "Transport", not "Bus" — a school may run vans or an auto, and the category names the service.
  TRANSPORT: 'Transport',
  LEAVE: 'Leave', // [M.1]
  FEES: 'Fees', // [Band F / F4] "Fees", not "Money" — it names the module the reminder comes from.
};

/** I18N-T5: leak-guarded label via the i18n singleton (common:notification_category.*) — the STUDENT
 *  notifications page uses this so its filter chips render Hindi; staff pages keep CATEGORY_LABELS. */
export function categoryLabel(cat: NotificationCategory): string {
  return i18n.t(`common:notification_category.${cat.toLowerCase()}`);
}

/** Types belonging to the CRITICAL category — convenience re-export for CriticalBanner. */
export const CRITICAL_TYPES: string[] = Object.entries(NOTIFICATION_CATEGORY)
  .filter(([, category]) => category === 'CRITICAL')
  .map(([type]) => type);

export function categoryOf(type: string): NotificationCategory | undefined {
  return NOTIFICATION_CATEGORY[type];
}
